import { useEffect } from "react";

import { buildTestId, getMeaningfulClassName, resolveTestId } from "app/common/testIds";

const CONTAINER_SELECTOR = [
  "main",
  "header",
  "nav",
  "aside",
  "footer",
  "form",
  "[role='dialog']",
  ".MuiDialog-paper",
  ".MuiDrawer-paper",
  ".MuiPopover-paper",
  ".MuiMenu-paper",
  ".modal-content",
  ".card",
  ".preview-container",
  ".kt-subheader",
  ".kt-header",
  ".kt-footer",
  ".kt-aside",
  ".kt-content",
  ".kt-portlet",
].join(", ");

const CONTROL_SELECTOR = [
  "button",
  "[role='button']",
  "a.btn",
  ".MuiButtonBase-root",
  "div.cursor-pointer",
  "span.cursor-pointer",
  "label.btn",
].join(", ");

const LABEL_SELECTOR = ["label", ".MuiFormLabel-root"].join(", ");
const ICON_SELECTOR = [
  "button svg",
  "[role='button'] svg",
  "a svg",
  "div.cursor-pointer svg",
  "span.cursor-pointer svg",
  ".MuiSvgIcon-root",
  ".svg-icon svg",
].join(", ");

const SEMANTIC_CONTAINER_NAMES = {
  MAIN: "main",
  HEADER: "header",
  NAV: "navigation",
  ASIDE: "sidebar",
  FOOTER: "footer",
  FORM: "form",
};

function collectMatchingNodes(root, selector) {
  if (!(root instanceof Element)) {
    return [];
  }

  const nodes = root.matches(selector) ? [root] : [];
  nodes.push(...root.querySelectorAll(selector));
  return nodes;
}

function getElementText(element, options = {}) {
  const { includeTextContent = true } = options;

  return resolveTestId(
    element?.getAttribute("aria-label"),
    element?.getAttribute("title"),
    element?.getAttribute("name"),
    element?.getAttribute("placeholder"),
    element?.getAttribute("alt"),
    element?.getAttribute("value"),
    element?.id,
    element?.htmlFor,
    includeTextContent ? element?.textContent : ""
  );
}

function getContainerTitle(element) {
  const titleNode = element.querySelector(
    "h1, h2, h3, h4, h5, h6, .modal-title, .card-title, .MuiDialogTitle-root, [data-title='true']"
  );

  return getElementText(titleNode);
}

function getContainerType(element) {
  if (
    element.matches(
      "[role='dialog'], .MuiDialog-paper, .MuiDrawer-paper, .MuiPopover-paper, .MuiMenu-paper, .modal-content"
    )
  ) {
    return "dialog";
  }

  if (element.matches(".card, .kt-portlet")) {
    return "card";
  }

  if (element.matches(".preview-container")) {
    return "preview";
  }

  return SEMANTIC_CONTAINER_NAMES[element.tagName] || "section";
}

function assignUniqueTestId(element, baseTestId) {
  if (!element || element.getAttribute("data-testid") || !baseTestId) {
    return;
  }

  let candidate = baseTestId;
  let suffix = 2;

  while (document.querySelector(`[data-testid="${candidate}"]`)) {
    candidate = `${baseTestId}-${suffix}`;
    suffix += 1;
  }

  element.setAttribute("data-testid", candidate);
}

function annotateContainers(root) {
  collectMatchingNodes(root, CONTAINER_SELECTOR).forEach((element) => {
    if (element.getAttribute("data-testid")) {
      return;
    }

    const containerType = getContainerType(element);
    const baseTestId = buildTestId(
      getElementText(element, { includeTextContent: false }),
      getContainerTitle(element),
      getMeaningfulClassName(element),
      containerType
    );

    assignUniqueTestId(element, baseTestId || containerType);
  });
}

function getControlType(element) {
  if (element.matches("label")) {
    return "label";
  }

  if (element.matches("a")) {
    return "link";
  }

  if (element.matches("div, span")) {
    return "action";
  }

  return "button";
}

function annotateControls(root) {
  collectMatchingNodes(root, CONTROL_SELECTOR).forEach((element) => {
    if (element.getAttribute("data-testid")) {
      return;
    }

    const controlType = getControlType(element);
    const parentTestId =
      element.parentElement?.closest("[data-testid]")?.getAttribute("data-testid") || "";
    const controlName =
      getElementText(element) ||
      resolveTestId(getMeaningfulClassName(element)) ||
      `${controlType}-${[...(element.parentElement?.children || [])].indexOf(element) + 1}`;

    assignUniqueTestId(element, buildTestId(parentTestId, controlName, controlType));
  });
}

function annotateLabels(root) {
  collectMatchingNodes(root, LABEL_SELECTOR).forEach((element) => {
    if (element.getAttribute("data-testid")) {
      return;
    }

    const parentTestId =
      element.parentElement?.closest("[data-testid]")?.getAttribute("data-testid") || "";
    const labelName =
      getElementText(element) ||
      resolveTestId(element.htmlFor, getMeaningfulClassName(element)) ||
      "label";

    assignUniqueTestId(element, buildTestId(parentTestId, labelName, "label"));
  });
}

function annotateIcons(root) {
  collectMatchingNodes(root, ICON_SELECTOR).forEach((element) => {
    if (element.getAttribute("data-testid")) {
      return;
    }

    const interactiveParent =
      element.parentElement?.closest(
        "button, [role='button'], a, div.cursor-pointer, span.cursor-pointer, [data-testid]"
      ) || null;
    const parentTestId =
      interactiveParent?.getAttribute("data-testid") ||
      interactiveParent?.parentElement?.closest("[data-testid]")?.getAttribute("data-testid") ||
      "";
    const iconName =
      resolveTestId(getElementText(element), getMeaningfulClassName(element)) || "icon";

    assignUniqueTestId(element, buildTestId(parentTestId, iconName, "icon"));
  });
}

function annotateTestIds(root) {
  annotateContainers(root);
  annotateControls(root);
  annotateLabels(root);
  annotateIcons(root);
}

export default function AutoTestIds() {
  useEffect(() => {
    if (typeof document === "undefined" || !document.body) {
      return undefined;
    }

    let frameId = null;
    const pendingRoots = new Set([document.body]);

    const flush = () => {
      frameId = null;

      pendingRoots.forEach((root) => {
        annotateTestIds(root);
      });

      pendingRoots.clear();
    };

    const schedule = (root) => {
      pendingRoots.add(root instanceof Element ? root : document.body);

      if (frameId !== null) {
        return;
      }

      frameId = window.requestAnimationFrame(flush);
    };

    schedule(document.body);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        schedule(mutation.target);
        mutation.addedNodes.forEach((node) => {
          schedule(node);
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      observer.disconnect();

      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, []);

  return null;
}
